import React, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
// components
import Button from '../buttons';
// eslint-disable-next-line no-unused-vars
import { Input, MaskedTextBox, InputProps, MaskedTextBoxProps } from '@progress/kendo-react-inputs';
import BaseCurrencyInput from './BaseCurrencyInput';
import BasePercentInput from './BasePercentInput';
import BaseNumericInput from './BaseNumericInput';
import isNil from 'lodash/isNil';

const getCustomInputComponent = (customInput) => {
  switch (customInput) {
  case 'numeric':
    return BaseNumericInput;
  case 'currency':
    return BaseCurrencyInput;
  case 'percent':
    return BasePercentInput;
  default:
    throw new Error(`Unknown custom input ${customInput}`);
  }
};

/**
 * @typedef {React.PropsWithoutRef<InputProps>} TelerikInputProps
 * @typedef {React.PropsWithoutRef<MaskedTextBoxProps>} TelerikMaskedTextBoxProps
 * @typedef {React.ComponentProps<BaseNumericInput>} BaseNumericInputProps
 *
 * @typedef {Object} AdditionalInputProps
 * @property {string} [dataTestId] Data test Id.
 * @property {'numeric' | 'currency' | 'percent'} [customInput] Type of the input.
 * @property {Function} [onEnterKeyPressed] A callback when enter key is pressed.
 * @property {React.ReactElement} [actionComponent] An element to add after the input.
 * @property {Function} [onQuickAdd] A callback when clicking Plus icon button. The button is added if the callback is set.
 * @property {boolean} [forceEnableQuickAdd] Always enable Plus icon button which calls onQuickAdd callback.
 * @property {Function} [handleBlur] An additional callback when the input is blurred.
 * @property {string} [icon] A Telerik icon name to add before the input.
 * @property {string} [goesTo] A link path to open a new browser tab, added after the input.
 * @property {boolean} [locked] Enables "locked" feture. The input is read only by default till unlocked by User.
 *
 * @param {TelerikInputProps & TelerikMaskedTextBoxProps & BaseNumericInputProps & AdditionalInputProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseInput = ({
  customInput,
  onEnterKeyPressed,
  onKeyDown,
  mask,
  actionComponent,
  onQuickAdd,
  forceEnableQuickAdd = false,
  disabled,
  handleBlur,
  value = '',
  icon,
  goesTo,
  // Requires unlock button to edit value
  locked = false,
  dataTestId,
  ...props
}, ref) => {
  const inputRef = useRef();
  const innerRef = ref ?? inputRef;

  const [isLocked, setIsLocked] = useState(locked);

  // Focus input on unlock
  useEffect(() => {
    if (locked && !isLocked && innerRef?.current) {
      innerRef.current.focus();
    }
  }, [isLocked]);

  const { readOnly = false, className } = props;

  const handleKeyDown = (e) => {
    // Enter
    if (e.keyCode === 13) {
      e.preventDefault(); // Ensure it is only this code that runs
      if (onEnterKeyPressed) {
        onEnterKeyPressed();
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  /**
   * Treatment for when both handleBlur and onBlur are implemented
   * When the input is inside a Kendo React form, the onBlur is always overwritten by the form
   * So when we want to implement an action when blur in this situation, we have to implement handleBlur
   */
  const onBlur = (e) => {
    if (handleBlur) {
      handleBlur(e);
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const onMaskedInputChange = useCallback((e) => {
    const { value, target } = e;

    // If value is equial to mask - set null
    if (mask.replaceAll('0', '*') === value) {
      props.onChange({ ...e, value: null, target: { ...target, value: null } });
    } else {
      props.onChange(e);
    }
  }, []);

  if (isLocked) {
    return (
      <div
        className='read-only'
        style={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}
        data-testid={dataTestId}
      >
        {value}
        {!readOnly && (
          <Button
            icon='edit'
            fillMode='flat'
            size='small'
            themeColor='tertiary'
            tooltip='Edit'
            onClick={() => setIsLocked(false)}
            tabIndex={-1}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  // Input with mask
  const renderInput = (extra) => {
    let input = (
      <Input
        ref={innerRef}
        onKeyDown={handleKeyDown}
        value={value || ''}
        disabled={disabled}
        {...props}
        onBlur={onBlur}
      />
    );

    const withIconClassName = cn(
      'k-input k-input-md k-rounded-md k-input-solid input-with-icon', { [className]: className },
    );

    if (icon) {
      input = (
        <div className={withIconClassName}>
          <span className={`k-icon k-i-${icon}`} />
          <Input
            ref={ref}
            onKeyDown={handleKeyDown}
            value={value || ''}
            disabled={disabled}
            {...props}
            onBlur={onBlur}
            className='k-input-inner'
          />
        </div>
      );
    }

    if (customInput) {
      const CustomInput = getCustomInputComponent(customInput);
      input = <CustomInput ref={ref} disabled={disabled} value={value} onKeyDown={handleKeyDown} {...props} />;
    }

    if (mask) {
      input = (
        <MaskedTextBox
          ref={innerRef}
          disabled={disabled}
          mask={mask}
          prompt="*"
          value={value ?? ''}
          onKeyDown={handleKeyDown}
          {...props}
          onChange={onMaskedInputChange}
        />
      );
    }

    return (
      <div className='input-items-container' data-testid={dataTestId}>
        {input}
        {extra}
      </div>
    );
  };

  if (onQuickAdd) {
    return renderInput(<Button
      icon='plus'
      type='button'
      fillMode='flat'
      themeColor='tertiary'
      disabled={disabled && !forceEnableQuickAdd}
      onClick={onQuickAdd}
      tabIndex={-1}
    />);
  }
  if (!forceEnableQuickAdd && !isNil(goesTo) && !isNil(value)) {
    return renderInput(
      <Button
        icon='hyperlink-open'
        type='button'
        fillMode='flat'
        themeColor='tertiary'
        onClick={() => {
          window.open(goesTo, '_blank');
        }}
        tabIndex={-1}
      />,
    );
  }
  if (actionComponent) {
    return renderInput(actionComponent);
  }

  return <span>{renderInput()}</span>;
};

/**
 * A base input component with various options and handlers.
 *
 * @param {TelerikInputProps & TelerikMaskedTextBoxProps & BaseNumericInputProps & AdditionalInputProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const ForwardInput = forwardRef(BaseInput);

export default ForwardInput;
